import React from 'react';

import { TrustSignalsBadgeContainer } from '@dbt-labs/metadata-shared';
import { SkeletonContextProvider, TextSubtext } from '@dbt-labs/sourdough';

import {
  AutoExposureBiProvider,
  FreshnessStatus,
  Quality,
  RunStatus,
} from '@/__generated__/graphql';
import {
  ExposureNode,
  useExposureTileQuery,
} from '@/hooks/discovery/useExposureTileQuery';
import StatusInformation from '../components/StatusInformation';
import DataMonitorFooter from './DataMonitorFooter';
import LastCheckedTime from './LastCheckedTime';

export enum TileStatus {
  unknown = 0,
  pass = 1,
  warn = 2,
  error = 3,
  skipped = 4,
}

type DataMonitorProps = {
  environmentId: number | null | undefined;
  uniqueId: string | null | undefined;
  environmentType: 'production' | 'staging' | 'development' | null | undefined;
  autoBiProvider?: AutoExposureBiProvider | null | undefined;
  biResourceId?: string | null | undefined;
  isLoading?: boolean;
};

function getModelStatus(exposure: ExposureNode) {
  const modelStats = exposure.upstreamStats?.models || {};
  const statusMap: Record<RunStatus, number> = {
    error: modelStats.error || 0,
    success: modelStats.success || 0,
    skipped: modelStats.skipped || 0,
    reused: modelStats.reused || 0,
  };

  const status = freshnessToTitleStatus(
    exposure.freshnessStatus || FreshnessStatus.Unknown,
  );
  return {
    status: freshnessToTitleStatus(exposure.freshnessStatus || FreshnessStatus.Unknown),
    text:
      status === TileStatus.unknown
        ? 'Model data unknown'
        : `Models: ${statusMap.success} success, ${statusMap.error} failure, ${statusMap.reused} reused, ${statusMap.skipped} skipped`,
  };
}

function freshnessToTitleStatus(freshness: FreshnessStatus): TileStatus {
  switch (freshness) {
    case FreshnessStatus.Error:
      return TileStatus.error;
    case FreshnessStatus.Pass:
      return TileStatus.pass;
    case FreshnessStatus.Warn:
      return TileStatus.warn;
    case FreshnessStatus.Skipped:
      return TileStatus.skipped;
    default:
      return TileStatus.unknown;
  }
}

function getSourceStatus(exposure: ExposureNode) {
  const freshnessStats = exposure.upstreamStats?.sources || {};
  const statusMap: Record<FreshnessStatus, number> = {
    Error: freshnessStats.error || 0,
    Pass: freshnessStats.pass || 0,
    Warn: freshnessStats.warn || 0,
    Skipped: freshnessStats.skipped || 0,
    Unknown: freshnessStats.missing || 0,
  };
  const status = freshnessToTitleStatus(
    exposure.freshnessStatus || FreshnessStatus.Unknown,
  );
  return {
    status,
    text:
      status === TileStatus.unknown
        ? 'Source data unknown'
        : `Sources: ${statusMap.Pass} fresh, ${statusMap.Warn} delayed, ${statusMap.Error} stale, ${statusMap.Skipped} skipped`,
  };
}

function qualityToTileStatus(quality: Quality): TileStatus {
  switch (quality) {
    case Quality.Error:
      return TileStatus.error;
    case Quality.Pass:
      return TileStatus.pass;
    case Quality.Warn:
      return TileStatus.warn;
    default:
      return TileStatus.unknown;
  }
}

type TestStatus = 'pass' | 'warn' | 'error';

function getTestStatus(exposure: ExposureNode) {
  const testStats = exposure.upstreamStats?.tests || {};
  const statusMap: Record<TestStatus, number> = {
    pass: testStats.pass || 0,
    warn: testStats.warn || 0,
    error: (testStats.error || 0) + (testStats.fail || 0),
  };
  const resourcesWithTests =
    exposure.upstreamStats?.num_upstream_resources_with_tests || 0;
  const totalResources = exposure.upstreamStats?.num_upstream_resources || 0;
  const testCoverage = totalResources ? resourcesWithTests / totalResources : 0;
  const status = qualityToTileStatus(exposure.quality || Quality.Unknown);
  return {
    status,
    text:
      status === TileStatus.unknown
        ? 'Test data unknown'
        : `Tests: ${statusMap.pass} passed, ${statusMap.warn} warnings, ${statusMap.error} errors`,
    testCoverageText: `Test coverage: ${Number(testCoverage).toLocaleString(undefined, {
      style: 'percent',
      minimumFractionDigits: 0,
    })}`,
  };
}

const determineDataFreshnessTitle = (freshnessStatus: FreshnessStatus): string => {
  switch (freshnessStatus) {
    case FreshnessStatus.Pass:
      return 'Data is fresh';
    case FreshnessStatus.Warn:
      return 'Data may be stale';
    case FreshnessStatus.Error:
      return 'Data is stale';
    case FreshnessStatus.Skipped:
      return 'Data freshness skipped';
    default:
      return 'Data freshness unknown';
  }
};

const determineDataQualityTitle = (tileStatus: Quality): string => {
  switch (tileStatus) {
    case Quality.Pass:
      return 'Data quality passed';
    case Quality.Warn:
      return 'Data quality warning';
    case Quality.Error:
      return 'Data quality degraded';
    default:
      return 'Data quality unknown';
  }
};

export const DataMonitor: React.FC<DataMonitorProps> = ({
  environmentId,
  uniqueId,
  environmentType,
  autoBiProvider,
  biResourceId,
  isLoading,
}) => {
  const [{ data, fetching, error }] = useExposureTileQuery({
    environmentId,
    uniqueId,
    autoBiProvider,
    biResourceId,
  });
  const showSkeleton = !!(fetching || isLoading);

  // TODO: re-enable this tracking after META-4299 is done

  /*
  const { source } = useDiscoveryContext();
  const { dbtInstance } = useDbtCloud();
  const [hasTrackedEvent, setHasTrackedEvent] = useState(false);
  useEffect(() => {
    if (!hasTrackedEvent && data && !showSkeleton) {
      trackEvent({
        name: 'metadata_exposure_tile_view',
        data: {
          url: window.location.href,
          name: data?.name,
          unique_id: data?.uniqueId,
          auto_bi_provider: data?.autoBiProvider,
          bi_resource_id: data?.meta.dbt_cloud_bi_resource_id,
          exposure_type: data?.exposureType,
          request_type: 'environment',
          account_id: data?.accountId,
          project_id: data?.projectId,
          job_definition_id: null,
          environment_id: data?.environmentId,
          created_at_utc: new Date().getTime(),
          source,
          user_agent: window.navigator.userAgent,
          tenant: dbtInstance,
        },
        schemaVersion: '1-0-0',
      });

      setHasTrackedEvent(true);
    }
  }, [data, showSkeleton, hasTrackedEvent, source, dbtInstance]);
  */

  if (!showSkeleton && error) {
    return (
      <div className="flex justify-center py-20">
        <p className="mx-12 text-center">{error.message}</p>
      </div>
    );
  }

  const hasAncestors = (data?.upstreamStats?.num_upstream_resources || 0) > 0;

  return (
    <div className="h-full w-full flex-1">
      {hasAncestors || showSkeleton ? (
        <DataMonitorBody exposure={data} isLoading={showSkeleton} />
      ) : (
        <div className="flex justify-center py-20">
          <p className="mx-12 text-center">
            Configure{' '}
            <a
              target="_blank"
              rel="noopener noreferrer"
              className="text-dbt-link-blue"
              href="https://docs.getdbt.com/docs/dbt-cloud/using-dbt-cloud/cloud-snapshotting-source-freshness/#enabling-source-freshness-snapshots"
            >
              source snapshot freshness
            </a>{' '}
            to view the health of your data.
          </p>
        </div>
      )}
      <DataMonitorFooter
        accountId={data?.accountId}
        projectId={data?.projectId}
        environmentId={environmentId}
        // unique id prop not guaranteed to be the actual node's unique id
        // so we need to use the one from the query result
        uniqueId={data?.uniqueId}
        environmentType={environmentType}
      />
    </div>
  );
};

type DataMonitorBodyProps = {
  exposure: ExposureNode | undefined | null;
  isLoading: boolean;
};

const DataMonitorBody = ({ exposure, isLoading }: DataMonitorBodyProps) => {
  // const freshnessStatus = exposure?.freshnessStatus || FreshnessStatus.Unknown;
  const freshnessTitle = determineDataFreshnessTitle(
    exposure?.freshnessStatus || FreshnessStatus.Unknown,
  );

  const qualityTitle = determineDataQualityTitle(exposure?.quality || Quality.Unknown);
  if (!exposure) {
    return;
  }
  const sourceStatus = getSourceStatus(exposure);
  const modelStatus = getModelStatus(exposure);
  const testStatus = getTestStatus(exposure);

  const freshnessStatus = freshnessToTitleStatus(
    exposure.freshnessStatus || FreshnessStatus.Unknown,
  );

  const qualityStatus = qualityToTileStatus(exposure.quality || Quality.Unknown);

  return (
    <>
      <SkeletonContextProvider isSkeleton={isLoading}>
        <div className="mt-4 flex flex-col gap-1 px-8">
          <div className="flex justify-between">
            <span>
              <h1 className="text-lg font-medium">Data health</h1>
            </span>
            <span>
              <TrustSignalsBadgeContainer
                resourceType="exposure"
                trustSignals={{
                  healthIssues: exposure?.healthIssues,
                }}
                shouldRenderStateHeader={true}
              />
            </span>
          </div>
          <div>
            <LastCheckedTime
              exposure={exposure}
              className="border-b border-dashed border-borderMuted pb-4"
            />
          </div>
        </div>
        <StatusInformation
          dataStatus={freshnessStatus}
          title={freshnessTitle}
          subtitle={
            <>
              <TextSubtext
                className="block w-full"
                size="xs"
                loadingDimensions={{
                  width: 200,
                  height: 12,
                }}
              >
                {sourceStatus.text}
              </TextSubtext>
            </>
          }
          className="border-b border-dashed border-borderMuted"
        />
        <StatusInformation
          dataStatus={qualityStatus}
          title={qualityTitle}
          subtitle={
            <>
              <TextSubtext
                className="block w-full"
                size="xs"
                loadingDimensions={{
                  width: 200,
                  height: 12,
                }}
              >
                {modelStatus.text}
              </TextSubtext>
              <TextSubtext
                className="block w-full"
                size="xs"
                loadingDimensions={{
                  width: 200,
                  height: 12,
                }}
              >
                {testStatus.text}
              </TextSubtext>
              <TextSubtext
                className="block w-full"
                size="xs"
                loadingDimensions={{
                  width: 200,
                  height: 12,
                }}
              >
                {testStatus.testCoverageText}
              </TextSubtext>
            </>
          }
        />
      </SkeletonContextProvider>
    </>
  );
};
